import { useProjectParams } from '@hooks/useProjectParams';
import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm, TableSettings } from 'components/TableSettings';
import { PriceListTableCount } from 'components/TotalCount/PriceListTableCount';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type { PriceListRatioCoefficientRowData } from 'gql/graphql';
import { ContractStatusEnum, ProjectStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { usePin } from 'hooks/usePin';
import { useProjectData } from 'hooks/useProjectData';
import { useSelection } from 'hooks/useSelection';
import { PageHeaderLayoutContext } from 'pages/Project/Common/Layouts/PageLayoutContext';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { CreateOrEditRatio } from 'pages/Project/UerProject/Ratio/CreateOrEditRatio/CreateOrEditRatio';
import { Button, Grid, GridItem, Layout } from 'uikit';

import type { ColDef, ColGroupDef, GridReadyEvent } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import cn from 'classnames';
import type { CSSProperties } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';

import { usePriceListColumns } from './factorsColumns';
import { priceListFactorsRemoveUnusedRows } from './factorsQueries';
import { usePriceListFactors } from './usePriceListFactors';

import Styles from './PriceListFactors.module.css';

export const PriceListFactors = () => {
    const { id: projectId, methodology } = useProjectParams();
    const { projectData } = useProjectData(projectId);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [position, setPosition] = useState<number | undefined>();
    const ref = useRef<AgGridReact<PriceListRatioCoefficientRowData>>(null);
    const tableSettingsRef = useRef<HTMLDivElement>(null);

    const { isHeaderOpened } = useContext(PageHeaderLayoutContext);

    const { priceList: priceListRules } = useAccess();

    const { columns, filterValues } = usePriceListColumns({ contractId: projectData?.contractId });

    const { datasource, totalCount, fetchedCount, refreshData } = usePriceListFactors({
        contractId: projectData?.contractId,
        filterValues,
        gridRef: ref,
    });

    useEffect(() => {
        if (typeof ref !== 'function' && ref?.current) {
            if (!totalCount) {
                ref?.current?.api?.showNoRowsOverlay();
            } else {
                ref?.current?.api?.hideOverlay();
            }
        }
    }, [totalCount, ref]);

    const [selection, , , setSelection] = useSelection('PriceListFactors');
    const [pinnedItems, , , setPinned] = usePin('PriceListFactors');

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const contractIdVariable = useMemo(
        () => ({
            contractId: projectData?.contractId,
        }),
        [projectData?.contractId],
    );

    const [, removeUnusedFactorsStatus, , removeUnusedFactorsRowsRequest] = useRequest({
        requestId: 'priceListFactorsRemoveUnusedRows',
        query: priceListFactorsRemoveUnusedRows,
        variables: contractIdVariable,
        withEffect: false,
    });

    const memoizedRequestIds = useMemo(
        () => [
            'uerPriceListRatioSectionNameFilter',
            'uerPriceListRatioGroupFullNameFilter',
            'uerPriceListRatioCodeFilter',
            'uerPriceListRatioWorkConditionsFilter',
            'uerPriceListRatioAppliesToFullNameFilter',
            'uerPriceListRatioWorkTypeFilter',
            'uerPriceListRatioStatusFilter',
        ],
        [],
    );

    const [resetPageData] = useResetMultipleStoreData({
        requestIds: memoizedRequestIds,
    });

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionQuery: removeUnusedFactorsRowsRequest,
            variables: contractIdVariable,
        });

    useEffect(
        () => () => {
            resetPageData();
        },
        [resetPageData],
    );

    useEffect(() => {
        if (removeUnusedFactorsStatus === RequestStatus.SUCCESS) {
            refreshData();
        }
    }, [dispatch, refreshData, removeUnusedFactorsStatus]);

    const breadCrumbsFactorsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectData?.uniqueId}/`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [methodology, projectData?.uniqueId, projectData?.name],
    );

    const isRemoving = removeUnusedFactorsStatus === RequestStatus.FETCHING;

    const isBtnDisabled = useMemo(
        () =>
            isRemoving ||
            projectData?.contractNavigation?.data?.contractNavData?.status !== ContractStatusEnum.InImplementation ||
            projectData?.status === ProjectStatusEnum.Delayed ||
            !priceListRules?.canDelete,
        [
            isRemoving,
            priceListRules?.canDelete,
            projectData?.contractNavigation?.data?.contractNavData?.status,
            projectData?.status,
        ],
    );

    const fullSetColumns = useMemo<(ColDef | ColGroupDef)[]>(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0 && !('hide' in column && column.hide)) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 6) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    );

    const onFullSetTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [fullSetSettingsColumns, setPinned, setSelection],
    );

    const handleModalClose = useCallback(() => {
        setTimeout(() => navigate('./'));
        refreshData();
    }, [navigate, refreshData]);

    const onGridReady = useCallback((event: GridReadyEvent) => {
        event.api.showNoRowsOverlay();
    }, []);

    useEffect(() => {
        if (tableSettingsRef.current) {
            setPosition(tableSettingsRef.current?.getBoundingClientRect().bottom);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableSettingsRef.current?.getBoundingClientRect().bottom, isHeaderOpened]);

    return (
        <ProjectPageLayout
            isHeaderCollapsable
            title="Прейскурант коэффициентов"
            breadCrumbsItems={breadCrumbsFactorsItems}
            headerBottom={
                <div className="wrapperToolbarLine">
                    <Layout>
                        <Button
                            onClick={handleOpenConfirmationDialog}
                            label="Удалить неиспользуемые записи"
                            view="secondary"
                            size="s"
                            disabled={isBtnDisabled}
                        />
                    </Layout>
                </div>
            }
        >
            <Routes>
                <Route
                    path="/editCoeff/:coeffId"
                    element={<CreateOrEditRatio onClose={handleModalClose} onConfirm={handleModalClose} />}
                />
            </Routes>
            <Layout direction="column">
                <>
                    <Grid cols={2} ref={tableSettingsRef}>
                        <GridItem>
                            <Grid cols={2} yAlign="center">
                                <GridItem style={{ display: 'flex' }}>
                                    <PriceListTableCount totalCount={fetchedCount} totalItems={totalCount} />
                                </GridItem>
                            </Grid>
                        </GridItem>
                        <GridItem style={{ display: 'flex', justifyContent: 'right' }}>
                            <TableSettings
                                columns={fullSetSettingsColumns as any}
                                onConfirm={onFullSetTableSettingsConfirm}
                                isColumnVisible={isColumnVisible}
                                showPinColumn
                                tableApi={ref?.current}
                            />
                        </GridItem>
                    </Grid>
                    {projectData?.contractId && (
                        <div
                            className={cn('ag-theme-alpine', Styles.priceListFactors)}
                            style={
                                {
                                    '--tableStartPosition': `${position ?? 0}px`,
                                } as CSSProperties
                            }
                        >
                            <AgGridTable
                                ref={ref}
                                datasource={datasource}
                                columnDefs={fullSetColumns}
                                rowModelType="infinite"
                                maxConcurrentDatasourceRequests={1}
                                maxBlocksInCache={50}
                                cacheBlockSize={50}
                                onGridReady={onGridReady}
                                suppressCellFocus
                                context={{
                                    tableUniqueId: 'factorsList',
                                }}
                            />
                        </div>
                    )}
                </>
            </Layout>
            <ConfirmationDialog
                textHeader="Удаление неиспользуемых записей"
                textConfirmation="Вы уверены, что хотите удалить записи Прейскуранта в статусе «Новая запись», неиспользуемые в Наряд-заказах и Актах КС-2?"
                confirmButtonText="Удалить"
                cancelButtonText="Не удалять"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ProjectPageLayout>
    );
};

PriceListFactors.displayName = 'PriceListFactors';
